import { useCallback, useEffect, useRef } from 'react'
import { useFeedback } from '@/app/providers/FeedbackContext'
import { useEscapeKey } from '@/common/hooks/useEscapeKey'
import { useIsCompactLayout } from '@/common/hooks'
import type { FeedbackType } from '@/types/feedback'

const feedbackTypes: { value: FeedbackType; label: string; icon: string }[] = [
  { value: 'bug_report', label: 'Bug Report', icon: '🐛' },
  { value: 'feature_request', label: 'Feature Request', icon: '💡' },
  { value: 'chat_response', label: 'Chat Issue', icon: '💬' },
  { value: 'error_report', label: 'Error Report', icon: '🚨' },
]

export function FeedbackModal() {
  const {
    open,
    type,
    description,
    sending,
    error,
    success,
    errorInfo,
    chatContext,
    screenshots,
    closeModal,
    setType,
    setDescription,
    addScreenshots,
    removeScreenshot,
    submit,
  } = useFeedback()

  const fileInputRef = useRef<HTMLInputElement>(null)
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const isMobile = useIsCompactLayout()

  const characterCount = description.length
  const disabled = sending || (characterCount === 0 && !errorInfo && !chatContext)

  useEscapeKey(closeModal, open)

  // Auto-grow the description box with its content, then let it scroll once it
  // reaches its max-height (set on the element below). offsetHeight - clientHeight
  // adds the borders so border-box sizing doesn't trip a premature scrollbar.
  const autoSizeTextarea = useCallback(() => {
    const el = textareaRef.current
    if (!el) return
    el.style.height = 'auto'
    el.style.height = `${el.scrollHeight + (el.offsetHeight - el.clientHeight)}px`
  }, [])

  useEffect(() => {
    if (open) autoSizeTextarea()
  }, [description, open, autoSizeTextarea])

  const handleSubmit = async () => {
    await submit()
  }

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files
    if (files && files.length > 0) {
      addScreenshots(files)
    }
    // Reset input so same file can be selected again
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  const handleRemoveScreenshot = (index: number) => {
    removeScreenshot(index)
  }

  if (!open) return null

  return (
    <>
      <div
        className="fixed inset-0 z-[100] bg-black/40 transition"
        onClick={closeModal}
      />

      <div
        className={`fixed inset-0 ${isMobile ? '' : 'md:left-[320px]'} z-[100] flex ${isMobile ? 'items-start justify-start' : 'items-center justify-center px-4'}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby="feedback-modal-title"
      >
        <div
          className={`w-full ${isMobile ? 'h-screen' : 'max-w-lg'} ${isMobile ? 'rounded-none' : 'rounded-3xl'} border p-6 shadow-card`}
          style={{ background: 'var(--color-surface)', borderColor: 'var(--color-border)', color: 'var(--color-text)' }}
        >
          <div className="flex items-center justify-between">
            <div>
              <h2 id="feedback-modal-title" className="text-lg font-semibold">Send Feedback</h2>
              <p className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>
                Help us improve XAVIOR
              </p>
            </div>
            <button
              type="button"
              onClick={closeModal}
              className="rounded-full border p-2 transition hover:border-accent hover:text-accent-strong"
              style={{ borderColor: 'var(--color-border)', color: 'var(--color-text-muted)' }}
            >
              <span className="sr-only">Close</span>
              <span className="text-lg">×</span>
            </button>
          </div>

          <div className="mt-4 flex flex-wrap gap-2">
            {feedbackTypes.map((ft) => (
              <button
                key={ft.value}
                type="button"
                onClick={() => setType(ft.value)}
                className={`flex items-center gap-1.5 rounded-full px-3 py-1.5 text-sm font-medium transition ${
                  type === ft.value ? 'bg-accent text-white' : ''
                }`}
                style={
                  type === ft.value
                    ? undefined
                    : { background: 'var(--color-surface-muted)', color: 'var(--color-text-secondary)' }
                }
              >
                <span>{ft.icon}</span>
                <span>{ft.label}</span>
              </button>
            ))}
          </div>

          {errorInfo && (
            <div className="mt-4 rounded-lg bg-red-50 border border-red-200 p-3">
              <p className="text-sm font-medium text-red-800">Error captured:</p>
              <p className="mt-1 text-sm text-red-700 font-mono truncate">
                {errorInfo.message}
              </p>
            </div>
          )}

          {chatContext && (
            <div className="mt-4 rounded-lg bg-blue-50 border border-blue-200 p-3">
              <p className="text-sm font-medium text-blue-800">About this response:</p>
              {chatContext.assistantResponse && (
                <p className="mt-1 text-sm text-blue-700 line-clamp-2">
                  "{chatContext.assistantResponse.slice(0, 150)}..."
                </p>
              )}
            </div>
          )}

          <textarea
            ref={textareaRef}
            value={description}
            onChange={(event) => setDescription(event.target.value)}
            placeholder={
              type === 'bug_report'
                ? "What happened? What did you expect?"
                : type === 'feature_request'
                ? "What would you like XAVIOR to do?"
                : type === 'chat_response'
                ? "What was wrong with this response?"
                : "Please describe the issue..."
            }
            rows={4}
            className="mt-4 w-full rounded-2xl border px-4 py-3 text-sm shadow-sm focus:border-accent focus:outline-none focus:ring-2 focus:ring-accent/20"
            // rows={4} is the floor; autoSizeTextarea grows it with content up to
            // maxHeight, after which it scrolls. resize:none — it sizes itself.
            style={{ background: 'var(--color-surface-muted)', borderColor: 'var(--color-border)', color: 'var(--color-text)', maxHeight: '220px', resize: 'none', overflowY: 'auto' }}
            disabled={sending}
          />

          <div className="mt-2 flex items-center justify-between text-xs" style={{ color: 'var(--color-text-muted)' }}>
            <span>{characterCount} / 2000</span>
            {error && <span className="text-rose-600">{error}</span>}
            {success && <span className="text-emerald-600">Thank you!</span>}
          </div>

          <div className="mt-4">
            {screenshots.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-2">
                {screenshots.map((screenshot, index) => (
                  <div key={index} className="relative inline-block">
                    <img
                      src={screenshot.preview}
                      alt={`Screenshot ${index + 1}`}
                      className="h-20 w-auto rounded-lg border object-cover"
                      style={{ borderColor: 'var(--color-border)' }}
                    />
                    <button
                      type="button"
                      onClick={() => handleRemoveScreenshot(index)}
                      className="absolute -top-2 -right-2 rounded-full bg-red-500 p-1 text-white text-xs hover:bg-red-600"
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            )}
            <label className="flex cursor-pointer items-center gap-2 text-sm hover:text-accent transition" style={{ color: 'var(--color-text-secondary)' }}>
              <span>📷</span>
              <span>{screenshots.length > 0 ? 'Add more screenshots' : 'Add screenshot'}</span>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                multiple
                onChange={handleFileSelect}
                className="hidden"
              />
            </label>
          </div>

          <div className="mt-5 flex items-center justify-end gap-3">
            <button
              type="button"
              onClick={closeModal}
              className="rounded-lg border px-4 py-2 text-sm font-medium transition hover:border-accent hover:text-accent-strong"
              style={{ borderColor: 'var(--color-border)', color: 'var(--color-text)' }}
              disabled={sending}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              className="rounded-lg px-4 py-2 text-sm font-semibold text-white shadow-sm transition disabled:cursor-not-allowed"
              // Theme brand, darkened ~25% so it isn't a glaring bright blue on the
              // dark surface; brightens to the full brand on hover. Disabled = muted.
              style={{
                background: disabled
                  ? 'var(--color-border)'
                  : 'color-mix(in srgb, var(--color-brand) 75%, #000)',
              }}
              onMouseEnter={(e) => {
                if (!disabled) e.currentTarget.style.background = 'var(--color-brand)';
              }}
              onMouseLeave={(e) => {
                if (!disabled) e.currentTarget.style.background = 'color-mix(in srgb, var(--color-brand) 75%, #000)';
              }}
              disabled={disabled}
            >
              {sending ? 'Sending...' : 'Send Feedback'}
            </button>
          </div>
        </div>
      </div>
    </>
  )
}
